import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Card,
	CardContent,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Chip,
	Typography,
	Box,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Divider,
	Grid
} from '@mui/material';
import Button1 from 'react-bootstrap/Button';
import { useNavigate } from 'react-router-dom';
import Spinner from '../component/Spinner';
import Toast from '../utils/toastManager';
import axiosConfig from '../data/utils/axiosConfigInstance';
import Pagination from '@/components/Pagination';
import './admin-cancellations.css';

export default function Cancellations() {
	axios.defaults.withCredentials = true;

	const [searchQuery, setSearchQuery] = useState('');
	const [pagination, setPagination] = useState({ page: 1, itemsPerPage: 10 });
	const [totalCancellations, setTotalCancellations] = useState(0);
	const [timeframe, setTimeframe] = useState('all');

	const [cancellations, setCancellations] = useState([]);
	const navigate = useNavigate();
	const [selectedCancellation, setSelectedCancellation] = useState(null);
	const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
	const [userDetails, setUserDetails] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [detailsLoading, setDetailsLoading] = useState(false);

	const [timeoutId, setTimeoutId] = useState(0);
	const [goFetchData, setGoFetchData] = useState(true);

	useEffect(() => {
		return () => clearTimeout(timeoutId);
	}, [timeoutId]);

	const showLoader = () => setIsLoading(true);
	const hideLoader = () => setIsLoading(false);

	const fetchLock = useRef(false);

	useEffect(() => {
		if (goFetchData && fetchLock.current === false) {
			fetchLock.current = true;
			setGoFetchData(false);

			showLoader();
			const apiUrl = `${import.meta.env.VITE_APP_API_URL}/admin/cancellations`;

			axiosConfig
				.get(apiUrl, {
					params: {
						search: searchQuery,
						page: pagination.page,
						limit: pagination.itemsPerPage
					}
				})
				.then(response => {
					if (response.status === 200) {
						const timer = setTimeout(() => {
							hideLoader();
							const data = response.data.data || response.data;
							setCancellations(data.records || []);
							setTotalCancellations(data.total || 0);
							fetchLock.current = false;
						}, 500);
						setTimeoutId(timer);
					} else if (response.status === 201) {
						Toast.error('Please Login Again');
						navigate('/login');
					}
				})
				.catch(() => {
					Toast.error('Something went wrong');
					fetchLock.current = false;
					hideLoader();
				});
		}
	}, [pagination, goFetchData]);

	const handleSearch = () => {
		setPagination({ page: 1, itemsPerPage: pagination.itemsPerPage });
		setGoFetchData(true);
	};

	const handlePageChange = page => {
		setPagination(prev => ({ ...prev, page }));
		setGoFetchData(true);
	};

	const handleItemsPerPageChange = itemsPerPage => {
		setPagination({ page: 1, itemsPerPage });
		setGoFetchData(true);
	};

	const handleTimeframeChange = event => {
		setTimeframe(event.target.value);
		setPagination({ page: 1, itemsPerPage: pagination.itemsPerPage });
		setGoFetchData(true);
	};

	const formatDateString = dateString => {
		const options = { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' };
		return new Date(dateString).toLocaleString('en-US', options);
	};

	const handleRowClick = async cancellation => {
		setSelectedCancellation(cancellation);
		setDetailsLoading(true);

		try {
			const detailsRes = await axiosConfig.get(
				`${import.meta.env.VITE_APP_API_URL}/admin/cancellation/${cancellation._id}`
			);
			const detailsData = detailsRes.data.data || detailsRes.data;
			setSelectedCancellation(detailsData);

			if (detailsData.email) {
				const userRes = await axiosConfig.get(`${import.meta.env.VITE_APP_API_URL}/admin/user/${detailsData.email}`);
				const userData = userRes.data.data || userRes.data;
				setUserDetails(userData);
			}
		} catch {
			Toast.error('Failed to load details');
		} finally {
			setDetailsLoading(false);
		}

		setOpenDetailsDialog(true);
	};

	const handleCloseDetails = () => {
		setOpenDetailsDialog(false);
		setSelectedCancellation(null);
		setUserDetails(null);
	};

	const getStatusChip = status => {
		const colorMap = {
			cancelled: 'error',
			scheduled: 'warning',
			active: 'success'
		};
		return <Chip label={status || 'N/A'} color={colorMap[status] || 'default'} size="small" />;
	};

	const getCancelOptionChip = option => {
		const colorMap = {
			immediately: 'error',
			end_of_term: 'warning',
			specific_date: 'info'
		};
		const labelMap = {
			immediately: 'Immediate',
			end_of_term: 'End of Term',
			specific_date: 'Specific Date'
		};
		return (
			<Chip
				label={labelMap[option] || option || 'N/A'}
				color={colorMap[option] || 'default'}
				size="small"
				variant="outlined"
			/>
		);
	};

	const formatReason = reason => {
		const labels = {
			too_expensive: 'Too Expensive',
			not_using: 'Not Using it Enough',
			taking_break: 'Taking a Break',
			too_complicated: 'Too Complicated',
			low_value: 'Low Value',
			technical: 'Technical Difficulties',
			other: 'Other'
		};
		return labels[reason] || reason || 'N/A';
	};

	const formatOfferType = type => {
		const labels = {
			discount_50_monthly: '50% Off (3 Months)',
			discount_25_annual: '25% Off Renewal (Annual)',
			discount_20_annual: '20% Off Renewal (Annual)',
			free_month_monthly: '1 Free Month (Monthly)',
			bonus_month_annual: '1 Bonus Month Added (Annual)',
			trial_extension_7d: '7-Day Trial Extension',
			pause_1_months: 'Pause Subscription (1 Month)',
			pause_2_months: 'Pause Subscription (2 Months)',
			pause_3_months: 'Pause Subscription (3 Months)',
			none: 'None'
		};
		return labels[type] || type || 'N/A';
	};

	const DetailField = ({ label, value, chip = false }) => (
		<Box sx={{ mb: 2 }}>
			<Typography
				variant="caption"
				sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
			>
				{label}
			</Typography>
			<Box sx={{ mt: 0.5 }}>
				{chip ? (
					value
				) : (
					<Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
						{value || 'N/A'}
					</Typography>
				)}
			</Box>
		</Box>
	);

	const handleExportCsv = async () => {
		try {
			const response = await axiosConfig.get(`${import.meta.env.VITE_APP_API_URL}/admin/cancellations/export`, {
				responseType: 'blob'
			});
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `cancellations-export-${new Date().toISOString().slice(0, 10)}.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			Toast.success('Export downloaded successfully');
		} catch {
			Toast.error('Failed to export cancellations');
		}
	};

	return (
		<div className="cancellations-container">
			<Box className="cancellations-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Box>
					<Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
						Cancellation Retention
					</Typography>
					<Typography variant="body2" sx={{ color: '#666' }}>
						Track and manage user cancellations
					</Typography>
				</Box>
				<Button1 variant="outline-primary" onClick={handleExportCsv}>
					Export CSV
				</Button1>
			</Box>

			<Card className="cancellations-filters-card">
				<CardContent>
					<Box className="filters-wrapper">
						<Box className="search-section">
							<input
								type="text"
								placeholder="Search by email or reason..."
								className="search-input"
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
								onKeyDown={e => {
									if (e.key === 'Enter') {
										handleSearch();
									}
								}}
							/>
							<Button1 variant="primary" onClick={handleSearch} className="search-btn">
								Search
							</Button1>
						</Box>

						<FormControl sx={{ minWidth: 200 }} size="small">
							<InputLabel>Timeframe</InputLabel>
							<Select value={timeframe} label="Timeframe" onChange={handleTimeframeChange}>
								<MenuItem value="all">All Time</MenuItem>
								<MenuItem value="7days">Last 7 Days</MenuItem>
								<MenuItem value="30days">Last 30 Days</MenuItem>
								<MenuItem value="90days">Last 90 Days</MenuItem>
							</Select>
						</FormControl>
					</Box>
				</CardContent>
			</Card>

			{isLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
					<Spinner />
				</Box>
			) : Array.isArray(cancellations) && cancellations.length > 0 ? (
				<>
					<Card className="cancellations-table-card">
						<CardContent sx={{ p: 0 }}>
							<Table>
								<TableHead>
									<TableRow className="table-header-row">
										<TableCell>Email</TableCell>
										<TableCell>Date</TableCell>
										<TableCell>Cancel Option</TableCell>
										<TableCell>Status</TableCell>
										<TableCell>Reason</TableCell>
										<TableCell>User</TableCell>
										<TableCell align="center">Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{cancellations.map(cancellation => (
										<TableRow key={cancellation._id} className="table-body-row">
											<TableCell className="email-cell">
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													{cancellation.email}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
													{formatDateString(cancellation.createdAt)}
												</Typography>
											</TableCell>
											<TableCell>{getCancelOptionChip(cancellation.cancelOption)}</TableCell>
											<TableCell>{getStatusChip(cancellation.statusAfter)}</TableCell>
											<TableCell>
												<Typography
													variant="body2"
													sx={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
												>
													{formatReason(cancellation.reason)}
												</Typography>
											</TableCell>
											<TableCell>
												{cancellation.user ? (
													<Typography variant="body2" sx={{ fontSize: '12px' }}>
														{cancellation.user.firstname} {cancellation.user.lastname}
													</Typography>
												) : (
													<Typography variant="body2" sx={{ fontSize: '12px', color: '#999' }}>
														No user
													</Typography>
												)}
											</TableCell>
											<TableCell align="center">
												<Button1
													variant="outline-primary"
													size="sm"
													onClick={() => handleRowClick(cancellation)}
													className="view-btn"
												>
													View
												</Button1>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					<Box className="pagination-wrapper">
						<Pagination
							currentPage={pagination.page}
							totalItems={totalCancellations}
							itemsPerPage={pagination.itemsPerPage}
							onPageChange={handlePageChange}
							onItemsPerPageChange={handleItemsPerPageChange}
						/>
					</Box>
				</>
			) : (
				!isLoading && (
					<Card>
						<CardContent>
							<Typography sx={{ textAlign: 'center', color: '#999' }}>No cancellations found.</Typography>
						</CardContent>
					</Card>
				)
			)}

			{/* Details Modal */}
			<Dialog open={openDetailsDialog} onClose={handleCloseDetails} maxWidth="md" fullWidth className="details-dialog">
				<DialogTitle
					sx={{
						background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
						color: 'white',
						fontWeight: 700,
						fontSize: '1.25rem',
						py: 2.5
					}}
				>
					Cancellation Details
				</DialogTitle>
				<DialogContent sx={{ pt: 3, pb: 3 }}>
					{detailsLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<Spinner />
						</Box>
					) : selectedCancellation ? (
						<Box className="modal-details-content">
							{/* Header Section */}
							<Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
								<Grid container spacing={2} alignItems="center">
									<Grid item xs={12} sm={8}>
										<Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
											{selectedCancellation.email}
										</Typography>
										<Typography variant="body2" sx={{ color: '#999' }}>
											{formatDateString(selectedCancellation.createdAt)}
										</Typography>
									</Grid>
									<Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
										{getStatusChip(selectedCancellation.statusAfter)}
									</Grid>
								</Grid>
							</Box>

							{/* User Information Section */}
							{userDetails && (
								<Box sx={{ mb: 3, p: 2, background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e3e6f0' }}>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 700, color: '#333', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
									>
										<Box sx={{ width: 4, height: 4, background: '#667eea', borderRadius: '50%' }} />
										User Profile
									</Typography>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<DetailField label="Name" value={`${userDetails.firstname} ${userDetails.lastname}`} />
										</Grid>
										<Grid item xs={12} sm={6}>
											<DetailField label="User Type" value={userDetails.userType || 'N/A'} />
										</Grid>
										<Grid item xs={12}>
											<DetailField label="Email" value={userDetails.email} />
										</Grid>
									</Grid>
								</Box>
							)}

							{/* Cancellation Details Section */}
							<Box sx={{ mb: 3, p: 2, background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
								<Typography
									variant="subtitle2"
									sx={{ fontWeight: 700, color: '#333', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
								>
									<Box sx={{ width: 4, height: 4, background: '#ffc107', borderRadius: '50%' }} />
									Cancellation Details
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<DetailField
											label="Cancel Option"
											value={getCancelOptionChip(selectedCancellation.cancelOption)}
											chip={true}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<DetailField label="Status Before" value={selectedCancellation.statusBefore || 'N/A'} />
									</Grid>
									{selectedCancellation.cancelDate && (
										<Grid item xs={12} sm={6}>
											<DetailField label="Cancel Date" value={selectedCancellation.cancelDate} />
										</Grid>
									)}
									{selectedCancellation.cancelEffectiveAt && (
										<Grid item xs={12} sm={6}>
											<DetailField
												label="Effective At"
												value={new Date(selectedCancellation.cancelEffectiveAt * 1000).toLocaleString()}
											/>
										</Grid>
									)}
								</Grid>
							</Box>

							{/* Feedback Section */}
							<Box sx={{ mb: 3, p: 2, background: '#e7f3ff', borderRadius: '8px', border: '1px solid #2196f3' }}>
								<Typography
									variant="subtitle2"
									sx={{ fontWeight: 700, color: '#333', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
								>
									<Box sx={{ width: 4, height: 4, background: '#2196f3', borderRadius: '50%' }} />
									Feedback & Insights
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<DetailField label="Cancellation Reason" value={formatReason(selectedCancellation.reason)} />
									</Grid>
									{selectedCancellation.otherText && (
										<Grid item xs={12}>
											<DetailField label="Additional Information" value={selectedCancellation.otherText} />
										</Grid>
									)}
									{selectedCancellation.likelihood && (
										<Grid item xs={12} sm={6}>
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
												>
													Likelihood to Return
												</Typography>
												<Box sx={{ mt: 0.5 }}>
													<Chip
														label={`${selectedCancellation.likelihood}/10`}
														color={
															selectedCancellation.likelihood >= 7
																? 'success'
																: selectedCancellation.likelihood >= 4
																	? 'warning'
																	: 'error'
														}
														size="small"
														sx={{ fontWeight: 600 }}
													/>
												</Box>
											</Box>
										</Grid>
									)}
									{selectedCancellation.feedback && (
										<Grid item xs={12}>
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
												>
													User Feedback
												</Typography>
												<Box
													sx={{
														mt: 0.5,
														p: 1.5,
														background: '#fff',
														border: '1px solid #ddd',
														borderRadius: '4px',
														maxHeight: '150px',
														overflow: 'auto'
													}}
												>
													<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#333' }}>
														{selectedCancellation.feedback}
													</Typography>
												</Box>
											</Box>
										</Grid>
									)}
								</Grid>
							</Box>

							{/* Retention Offer Section */}
							{(selectedCancellation.couponOffered || selectedCancellation.offerOffered) && (
								<Box sx={{ mb: 3, p: 2, background: '#f0f9ff', borderRadius: '8px', border: '1px solid #10b981' }}>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 700, color: '#333', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
									>
										<Box sx={{ width: 4, height: 4, background: '#10b981', borderRadius: '50%' }} />
										Retention Offer
									</Typography>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
												>
													Offer Shown
												</Typography>
												<Box sx={{ mt: 0.5 }}>
													<Chip label="Yes" color="success" size="small" sx={{ fontWeight: 600 }} />
												</Box>
											</Box>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
												>
													Offer Accepted
												</Typography>
												<Box sx={{ mt: 0.5 }}>
													<Chip
														label={
															selectedCancellation.couponApplied || selectedCancellation.offerAccepted
																? 'Yes'
																: 'No (Declined)'
														}
														color={
															selectedCancellation.couponApplied || selectedCancellation.offerAccepted
																? 'success'
																: 'default'
														}
														size="small"
														sx={{ fontWeight: 600 }}
													/>
												</Box>
											</Box>
										</Grid>
										{selectedCancellation.offerType && (
											<Grid item xs={12}>
												<DetailField label="Offer Type" value={formatOfferType(selectedCancellation.offerType)} />
											</Grid>
										)}
									</Grid>
								</Box>
							)}

							{/* Cancellation History Section */}
							{userDetails?.cancellationHistory && userDetails.cancellationHistory.length > 0 && (
								<Box sx={{ p: 2, background: '#f5f5f5', borderRadius: '8px', border: '1px solid #ddd' }}>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 700, color: '#333', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
									>
										<Box sx={{ width: 4, height: 4, background: '#666', borderRadius: '50%' }} />
										Cancellation History ({userDetails.cancellationHistory.length})
									</Typography>
									<Box sx={{ maxHeight: '250px', overflow: 'auto' }}>
										{userDetails.cancellationHistory.map((item, idx) => (
											<Box
												key={idx}
												sx={{
													mb: 1.5,
													pb: 1.5,
													borderBottom: idx !== userDetails.cancellationHistory.length - 1 ? '1px solid #ddd' : 'none',
													'&:last-child': { borderBottom: 'none' }
												}}
											>
												<Box
													sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}
												>
													<Box>
														<Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
															{formatDateString(item.createdAt)}
														</Typography>
														<Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
															{item.reason || 'No reason provided'}
														</Typography>
													</Box>
													<Chip label={item.cancelOption || 'N/A'} size="small" variant="outlined" />
												</Box>
											</Box>
										))}
									</Box>
								</Box>
							)}
						</Box>
					) : null}
				</DialogContent>
				<DialogActions sx={{ borderTop: '1px solid #e3e6f0', p: 2, background: '#f8f9fa' }}>
					<Button1 onClick={handleCloseDetails} variant="secondary">
						Close
					</Button1>
				</DialogActions>
			</Dialog>
		</div>
	);
}
